import MakeTool from '../MakeTool.js';
import { modal } from '../../modal.js';
import { uploadImage } from '../../../utils/imageUpload.js';

class ImageTool extends MakeTool {
    constructor(editor, config = {}) {
        super(editor, 'Image link');
        this.config = config;
        this.fileInputConfig = config.fileInput || false;
        // Logic: Respect config or default to true
        this.altInputConfig = config.altInput !== undefined ? config.altInput : { required: true };
        
        this.button = this.createButton(`
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M15 8h.01" /><path d="M12.5 21h-6.5a3 3 0 0 1 -3 -3v-12a3 3 0 0 1 3 -3h12a3 3 0 0 1 3 3v6.5" /><path d="M3 16l5 -5c.928 -.893 2.072 -.893 3 0l4 4" /><path d="M14 14l1 -1c.67 -.644 1.45 -.824 2.182 -.54" /><path d="M16 19h6" /><path d="M19 16v6" /></svg>
        `);
    }

    applySyntax(event) {
        const editor = this.editor;
        const textarea = editor.usertextarea;
        let { selectionStart, selectionEnd } = textarea;
        const fullText = textarea.value;

        // Auto-expand selection if the selected text is inside an existing image ![alt](url)
        const before = fullText.substring(0, selectionStart);
        const after = fullText.substring(selectionEnd);
        if (before.endsWith('![') && /^\]\([^)]+\)/.test(after)) {
            const urlMatch = after.match(/^\]\(([^)]+)\)/);
            if (urlMatch) {
                selectionStart = before.length - 2;
                selectionEnd = selectionEnd + urlMatch[0].length;
                textarea.setSelectionRange(selectionStart, selectionEnd);
            }
        }

        const selectedText = fullText.substring(selectionStart, selectionEnd);

        const acceptFormats = this.fileInputConfig.accept
            ? this.fileInputConfig.accept.map(type => `image/${type}`).join(',')
            : 'image/*';

        const isAltRequired = typeof this.altInputConfig === 'boolean'
            ? this.altInputConfig
            : (this.altInputConfig.required !== false);

        const uploadUrl = this.fileInputConfig.uploadUrl || null;
        const customParams = this.fileInputConfig.params || {};

        const imageRegex = /^!\[([^\]]*)\]\(([^)]+)\)$/;
        const imageMatch = selectedText && selectedText.match(imageRegex);
        const prefillAlt = imageMatch ? imageMatch[1] : selectedText;
        const prefillUrl  = imageMatch ? imageMatch[2] : '';

        let fileInputTag = '';
        if (this.fileInputConfig) {
            fileInputTag = `
                <input type="file" accept="${acceptFormats}" class="img-file-input fj:me-input fj:w-full">
                <div class="fj:me-divider fj:my-1">OR</div>
            `;
        }

        const bodyHTML = `
            <div class="fj:flex fj:justify-between fj:items-center fj:gap-3">
                <div class="fj:font-medium">${editor.label('Image')}</div>
                <button type="button" class="modal-close-btn fj:me-btn fj:me-btn-ghost fj:me-btn-xs fj:me-btn-circle" aria-label="${editor.label('Close')}">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                </button>
            </div>
            <div class="fj:mt-4 fj:flex fj:flex-col fj:gap-y-4">
                ${fileInputTag}
                <input type="url" placeholder="${editor.label('URL')}" class="img-link-input fj:me-input fj:w-full" value="${prefillUrl}" required>
                <input type="text" placeholder="${editor.label('Alt text')}" class="img-link-alt-input fj:me-input fj:w-full" value="${prefillAlt}" ${isAltRequired ? 'required' : ''}>
                <button type="button" class="submit-img-link fj:me-btn fj:me-btn-sm fj:self-end">${editor.label('Apply')}</button>
            </div>`;

        const modalElement = modal(event, 'fj:max-w-sm', bodyHTML, 'Image');
        modalElement.querySelector(".modal-close-btn").addEventListener("click", () => modalElement.close());
        const submitBtn = modalElement.querySelector(".submit-img-link");

        submitBtn.addEventListener("click", async (e) => {
            e.preventDefault();

            const fileInputSelector = modalElement.querySelector(".img-file-input");
            const urlInputSelector = modalElement.querySelector(".img-link-input");
            const imgFileAltInput = modalElement.querySelector(".img-link-alt-input");

            const hasFile = fileInputSelector?.files[0];

            // --- 1. PRE-VALIDATION ---
            // If we are uploading a file, the URL is currently empty, so we temporarily
            // disable the 'required' on URL just to check the Alt text.
            if (hasFile) {
                urlInputSelector.required = false; 
            }

            // Check if Alt (and URL if no file) are valid according to config
            if (isAltRequired && !imgFileAltInput.validity.valid) {
                imgFileAltInput.reportValidity();
                if (hasFile) urlInputSelector.required = true; // Restore
                return;
            }

            if (!hasFile && !urlInputSelector.validity.valid) {
                urlInputSelector.reportValidity();
                return;
            }

            // --- 2. ASYNC UPLOAD ---
            if (hasFile && uploadUrl) {
                const file = fileInputSelector.files[0];
                submitBtn.disabled = true;
                submitBtn.textContent = editor.label('Uploading...');

                try {
                    const { path, alt } = await uploadImage(file, {
                        uploadUrl,
                        params: customParams,
                        alt: imgFileAltInput.value,
                    });

                    urlInputSelector.value = path;
                    // Only take the server's suggestion when the user wrote nothing,
                    // so a typed description is never silently replaced.
                    if (alt && !imgFileAltInput.value.trim()) imgFileAltInput.value = alt;
                } catch (err) {
                    alert(err.message || 'Upload failed.');
                    submitBtn.disabled = false;
                    submitBtn.textContent = editor.label('Apply');
                    urlInputSelector.required = true; // Restore
                    return;
                }

                submitBtn.disabled = false;
                submitBtn.textContent = editor.label('Apply');
            }

            // --- 3. FINAL INSERTION ---
            const imgLink = urlInputSelector.value;
            const imgLinkAlt = imgFileAltInput.value || '';
            const newText = `![${imgLinkAlt}](${imgLink})`;

            editor.insertText(newText);
            modalElement.close();
        });
    }
}

export default ImageTool;